import { useClient, useQuery } from "jsonapi-react";
import React, { useContext, useEffect, useState } from "react";
import { apiRoutes } from "../../constants";
import DataTable from "react-data-table-component";
import ProfileCard from "../../components/ProfileCard/ProfileCard";
import { formatTextWithFallback } from "../../helpers/helpers";
import ReactTooltip from "react-tooltip";
import { AppContext } from "../../context/AppContext";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import CustomTooltip from "../../components/common/CustomTooltip/CustomTooltip";

const UserLoginHistoryListing = ({ userId, name, from }) => {
  const history = useHistory();
  const { setProfilePageUrlParams } = useContext(AppContext);
  const params = new URLSearchParams(history?.location?.search);

  const [rowsPerPage, setRowsPerPage] = useState(params.get("size") ?? 10);
  const [currPage, setCurrPage] = useState(params.get("page") ?? 1);
  const [defaultPage, setDefaultPage] = useState(params.get("page") ?? 1);

  const token = localStorage.getItem("access_token");
  const client = useClient();
  client.config.headers = {
    Authorization: `JWT ${token}`,
  };

  const loginHistory = useQuery(
    userId && [
      apiRoutes.userLoginHistory.replace(":user_id", userId),
      {
        filter: { activity_type: "login" },
        page: {
          number: currPage,
          size: rowsPerPage,
        },
      },
    ],
    { client }
  );

  const handleRowsPerPageChange = (rows) => {
    const params = {
      id: userId,
      name,
      from,
      page: currPage,
      size: rows,
    };
    setRowsPerPage(rows);
    setProfilePageUrlParams(params);
  };
  const handleCurrPageChange = (page) => {
    const params = {
      id: userId,
      name,
      from,
      page: page,
      size: rowsPerPage,
    };
    setCurrPage(page);
    setProfilePageUrlParams(params);
  };

  useEffect(() => {
    setCurrPage(params.get("page") ?? 1);
    setRowsPerPage(params.get("size") ?? 10);
    setDefaultPage(params.get("page") ?? 1);
  }, []);

  const formatBrowserRetails = (browserData = "") =>
    String(browserData).split(")").join(`)\n`);

  const renderProfileCard = (rowData) => (
    <ProfileCard
      data={rowData}
      mode="user-login-history"
      fields={[
        {
          name: "IP Address",
          value: (
            <p className="d-inline m-0 text-wrap">
              {formatTextWithFallback(rowData?.userData?.ipAddress)}
              {String(rowData?.userData?.ipAddress ?? "").length > 0 &&
                String(rowData?.userData?.ipVersion ?? "").length > 0 && (
                  <>
                    <span className="mr-1" />
                    <CustomTooltip
                      content={rowData?.userData?.ipVersion ?? ""}
                      className="d-inline"
                      tooltipClassName="text-break"
                    >
                      <i className="iconsminds-information info-icon-small" />
                    </CustomTooltip>
                  </>
                )}
            </p>
          ),
        },
        {
          name: "Location",
          value: formatTextWithFallback(rowData?.location),
        },
        {
          name: "Browser",
          value: (
            <p className="d-inline m-0">
              {String(rowData?.userData?.userAgent ?? "").length === 0 &&
              String(rowData?.userData?.osName ?? "").length === 0
                ? "--"
                : ""}
              {`${rowData?.userData?.userAgent ?? ""}${
                String(rowData?.userData?.osName ?? "").length > 0
                  ? ", " + rowData?.userData?.osName
                  : ""
              }`}
              {String(rowData?.userData?.browserDetails ?? "").length > 0 && (
                <>
                  <span className="mr-1" />
                  <CustomTooltip
                    content={
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {formatBrowserRetails(
                          rowData?.userData?.browserDetails
                        )}
                      </span>
                    }
                    className="d-inline"
                    tooltipClassName="text-break"
                  >
                    <i className="iconsminds-information info-icon-small" />
                  </CustomTooltip>
                </>
              )}
            </p>
          ),
        },
      ]}
    />
  );

  const columns = [
    {
      name: "Timestamp",
      minWidth: 200,
      selector: (row) => row["activityTime"],
      cell: (row) => renderProfileCard(row),
      grow: 1,
    },
    {
      name: "IP Address",
      minWidth: 320,
      selector: (row) => row[("userData", "ipAddress")],
      cell: (row) => (
        <p className="m-0">
          {formatTextWithFallback(row?.userData?.ipAddress)}
          {String(row?.userData?.ipAddress ?? "").length > 0 &&
            String(row?.userData?.ipVersion ?? "").length > 0 && (
              <>
                <span className="mr-1" />
                <CustomTooltip
                  content={row?.userData?.ipVersion ?? ""}
                  className="d-inline"
                  tooltipClassName="text-break"
                >
                  <i className="iconsminds-information info-icon-small" />
                </CustomTooltip>
              </>
            )}
        </p>
      ),
      hide: "md",
      grow: 2,
    },
    {
      name: "Location",
      minWidth: 150,
      selector: (row) => row["location"],
      cell: (row) => formatTextWithFallback(row?.location),
      hide: "md",
      grow: 2,
    },

    {
      name: "Browser",
      minWidth: 150,
      grow: 2,
      selector: (row) => row["userAgent"],
      cell: (row) => (
        <p className="m-0">
          {String(row?.userData?.userAgent ?? "").length === 0 &&
          String(row?.userData?.osName ?? "").length === 0
            ? "--"
            : ""}
          {`${row?.userData?.userAgent ?? ""}${
            String(row?.userData?.osName ?? "").length > 0
              ? ", " + row?.userData?.osName
              : ""
          }`}
          {String(row?.userData?.browserDetails ?? "").length > 0 && (
            <>
              <span className="mr-1" />
              <CustomTooltip
                content={
                  <span style={{ whiteSpace: "pre-wrap" }}>
                    {formatBrowserRetails(row?.userData?.browserDetails)}
                  </span>
                }
                className="d-inline"
                tooltipClassName="text-break"
              >
                <i className="iconsminds-information info-icon-small" />
              </CustomTooltip>
            </>
          )}
        </p>
      ),
      hide: "md",
    },
  ];

  return (
    <div className="row">
      <div className="col-lg-12 col-md-12">
        <div className="card">
          <div
            className={
              loginHistory?.isLoading || loginHistory?.isFetching
                ? "card-body dataTableOuter table-loader"
                : "card-body dataTableOuter"
            }
          >
            <DataTable
              subHeader={true}
              keyField="id"
              progressComponent={<p>&nbsp;</p>}
              progressPending={
                loginHistory?.isLoading || loginHistory?.isFetching
              }
              columns={columns}
              data={loginHistory?.data}
              paginationServer={true}
              pagination={true}
              paginationPerPage={rowsPerPage}
              onChangeRowsPerPage={handleRowsPerPageChange}
              onChangePage={handleCurrPageChange}
              paginationTotalRows={
                loginHistory?.meta && loginHistory?.meta?.pagination?.count
              }
              striped={true}
              paginationDefaultPage={defaultPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLoginHistoryListing;
